import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Activity, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { quantStrategyPerformanceLive, getErrorMessage } from '../../lib/api';

/**
 * PerformanceLiveCard — Confronto Live vs Backtest + Drift badge. Polling 60s.
 *
 * Props:
 *   - strategy: string (nome strategia)
 *   - window: number (default 30 trade)
 *
 * Layout 2 colonne:
 *   Live              Backtest
 *   Sharpe  1.4       Sharpe  1.8
 *   Win%    62%       Win%    59%
 *   ...               ...
 *
 *   Badge severity top-right: "Drift 2.1σ" (ok=verde, warn=giallo, alert=rosso, insufficient_data=grigio)
 */

const fmtNum = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));
const fmtPct = (v) => (v == null ? '—' : `${(Number(v) * 100).toFixed(1)}%`);

const SEVERITY_STYLES = {
  ok: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  warn: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  alert: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  insufficient_data: 'bg-muted text-muted-foreground',
};

const MetricRow = ({ label, liveVal, btVal }) => (
  <div className="grid grid-cols-3 gap-2 py-1 border-b last:border-0 border-muted/50 text-xs">
    <span className="text-muted-foreground">{label}</span>
    <span className="tabular-nums font-mono text-center">{liveVal}</span>
    <span className="tabular-nums font-mono text-center text-muted-foreground">{btVal}</span>
  </div>
);

const PerformanceLiveCard = ({ strategy, window: tradeWindow = 30 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  const fetchPerf = useCallback(async () => {
    if (!strategy) return;
    setLoading(true);
    setError(null);
    try {
      const r = await quantStrategyPerformanceLive(strategy, tradeWindow);
      setData(r.data);
    } catch (e) {
      if (e.response?.status === 404) {
        setData(null); // Nessuna serie live disponibile — silenzio
      } else {
        setError(getErrorMessage(e, 'Errore performance live'));
      }
    } finally {
      setLoading(false);
    }
  }, [strategy, tradeWindow]);

  useEffect(() => {
    fetchPerf();
    timerRef.current = setInterval(fetchPerf, 60000);
    return () => clearInterval(timerRef.current);
  }, [fetchPerf]);

  if (!strategy) return null;

  const severity = data?.divergence?.severity || 'insufficient_data';
  const sharpeZ = data?.divergence?.sharpe_z;
  const live = data?.live;
  const bt = data?.backtest;
  const insufficientSample = live?.insufficient_sample;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <Activity className={`w-4 h-4 ${loading ? 'animate-pulse text-primary' : 'text-primary'}`} />
            Performance live
            <span className="font-mono text-xs text-muted-foreground">{strategy}</span>
          </span>
          <div className="flex items-center gap-2">
            {/* Drift badge */}
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${SEVERITY_STYLES[severity] || SEVERITY_STYLES.insufficient_data}`}>
              {severity === 'insufficient_data'
                ? 'Dati insufficienti'
                : `Drift ${sharpeZ != null ? Math.abs(sharpeZ).toFixed(1) : '?'}σ`}
            </span>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={fetchPerf} disabled={loading}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent className="pt-0">
        {loading && !data ? (
          <div className="h-32 bg-muted/40 animate-pulse rounded" />
        ) : error ? (
          <p className="text-xs text-red-500">{error}</p>
        ) : insufficientSample ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Servono ≥5 trade chiusi per calcolare le metriche live.
          </div>
        ) : data ? (
          <>
            {/* Header colonne */}
            <div className="grid grid-cols-3 gap-2 pb-1 mb-1 text-[10px] uppercase tracking-wide text-muted-foreground font-semibold border-b">
              <span>Metrica</span>
              <span className="text-center text-primary">Live ({data.window_trades} trade)</span>
              <span className="text-center">Backtest</span>
            </div>
            <MetricRow
              label="Sharpe"
              liveVal={fmtNum(live?.sharpe)}
              btVal={fmtNum(bt?.sharpe)}
            />
            <MetricRow
              label="Win rate"
              liveVal={fmtPct(live?.win_rate)}
              btVal={fmtPct(bt?.win_rate)}
            />
            <MetricRow
              label="Profit factor"
              liveVal={fmtNum(live?.profit_factor)}
              btVal={fmtNum(bt?.profit_factor)}
            />
            <MetricRow
              label="Avg holding"
              liveVal={live?.avg_holding != null ? `${fmtNum(live.avg_holding, 1)}gg` : '—'}
              btVal={bt?.avg_holding != null ? `${fmtNum(bt.avg_holding, 1)}gg` : '—'}
            />
            <MetricRow
              label="Max DD"
              liveVal={fmtPct(live?.max_dd)}
              btVal={fmtPct(bt?.max_dd)}
            />
          </>
        ) : (
          <p className="text-xs text-muted-foreground italic py-3">
            Nessun dato performance live disponibile.
          </p>
        )}
        {data && (
          <p className="text-[9px] text-muted-foreground mt-2 font-mono">
            auto-refresh 60s · finestra {tradeWindow} trade
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default PerformanceLiveCard;
